export type LetterFormat = 'letter' | 'postcard';

/**
 * Editable data for a campaign letter / postcard. Stored on
 * `doorsteps_campaigns.template_data` and consumed by both the in-app
 * PDF preview and the dispatch edge function's HTML renderer.
 *
 * Personalisation tokens supported in headline / body / cta_text:
 *   {{street}}, {{town}}, {{postcode}}, {{address_line_1}}, {{agency_name}},
 *   {{agent_name}}, {{contact_phone}}
 */
export interface LetterTemplateData {
  format: LetterFormat;
  template_id: string;
  headline: string;
  body: string;             // multi-paragraph; \n\n separated
  cta_text: string;
  primary_color: string;    // e.g. '#0a0a0a'
  photo_url: string | null; // optional hero photo / agent headshot
  show_comparable: boolean; // whether to inject the recent-sale snippet
  signature_line: string;
}

export interface LetterTemplate {
  id: string;
  label: string;
  description: string;
  format: LetterFormat;
  defaults: LetterTemplateData;
  thumbnail: 'classic_letter' | 'modern_letter' | 'bold_postcard';
}

export const DEFAULT_TEMPLATE_DATA: Omit<LetterTemplateData, 'template_id' | 'format'> = {
  headline: "What's your home on {{street}} worth today?",
  body:
    "Hello,\n\n" +
    "I'm {{agent_name}} from {{agency_name}}. The market on your street has shifted recently and I thought it was worth letting you know what's been happening locally.\n\n" +
    "If you're curious what your home might be worth in today's market — even if you're not actively thinking of moving — I can prepare a no-obligation valuation.",
  cta_text: 'Scan the QR code or visit the link below to request your valuation.',
  primary_color: '#0f172a',
  photo_url: null,
  show_comparable: true,
  signature_line: 'Kind regards,\n{{agent_name}}',
};

/**
 * Replace personalisation tokens in a string. Used by both renderers.
 * Pass safe substitution values; never raw user input from the landing page.
 */
export function applyTokens(
  text: string,
  tokens: Record<string, string | null | undefined>
): string {
  return text.replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (_, key) => {
    const v = tokens[key];
    return v == null ? '' : String(v);
  });
}
